import { useAuthContext } from '../providers/AuthContext';
import { useState } from 'react';
import { useForm } from "react-hook-form";
import { Redirect } from 'react-router';
import { Link } from 'react-router-dom';
import { PapErrors, PapInput } from '../components/FormElements';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion } from 'framer-motion';
import { LogIn } from 'lucide-react';

import SocialLogin from '../components/Social/SocialLogin';
import AuthLayout from '../components/AuthLayout';
import useQuery from '../hooks/useQuery';

interface LoginFormData {
  email: string;
  password: string;
}

const schema = yup.object().shape({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.23, 1, 0.32, 1] as const } },
};

const Page: React.FC = () => {
  const query = useQuery();
  const {login, user} = useAuthContext();
  const [authErrors, setAuthErrors] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const signIn = async (data: LoginFormData) => {
    setAuthErrors([]);
    try {
      await login(data.email, data.password);
      reset();
    } catch (ex: any) {
      if (ex.response?.error == 'invalid_grant') {
        setAuthErrors(['Invalid email or password.']);
      } else {
        setAuthErrors([ex.response?.error_description || 'Login failed']);
      }
    }
  };

  return !user ? (
    <AuthLayout>
      <motion.div variants={stagger} initial="hidden" animate="show">
        {/* Logo & tagline */}
        <motion.div variants={fadeUp} className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-foreground mb-1.5">
            Plan-A-Play
          </h1>
          <p className="text-sm text-muted-foreground">Fun Made Easy</p>
        </motion.div>

        {/* Social login */}
        <motion.div variants={fadeUp} className="mb-6">
          <SocialLogin />
        </motion.div>

        {/* Divider */}
        <motion.div variants={fadeUp} className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border/60" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-card/90 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              or continue with email
            </span>
          </div>
        </motion.div>

        {/* Alerts */}
        {authErrors.length > 0 && (
          <motion.div variants={fadeUp}>
            <PapErrors errors={authErrors} />
          </motion.div>
        )}

        {query.get('reset') == '1' && (
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2.5 p-3.5 bg-success/[0.06] dark:bg-success/[0.1] border border-success/20 rounded-xl mb-5 text-sm text-success">
              <span className="font-medium">Your password has been reset. Please login with your new password.</span>
            </div>
          </motion.div>
        )}

        {(query.get('error') || query.get('error_description')) && (
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2.5 p-3.5 bg-destructive/[0.06] dark:bg-destructive/[0.12] border border-destructive/20 rounded-xl mb-5 text-sm text-destructive">
              <span className="font-medium">
                {query.get('error_description') || 'Unable to connect your account to an external provider, please try again.'}
              </span>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <motion.form variants={fadeUp} noValidate onSubmit={handleSubmit(signIn)} method="post">
          <PapInput fieldLabel="Email" type="email" formBinding={register("email")} required={true} placeholder="" fieldName="email" error={errors.email} />
          <PapInput fieldLabel="Password" type="password" formBinding={register("password")} required={true} placeholder="" fieldName="password" error={errors.password} />

          <div className="flex items-center justify-end mb-5">
            <Link to="/forgot-password" className="text-xs text-accent hover:text-accent/80 hover:underline font-medium transition-colors">
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary btn-block py-3 text-base font-semibold gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              <LogIn className="w-4 h-4" />
            )}
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </motion.form>

        {/* Register link */}
        <motion.p variants={fadeUp} className="text-center text-sm text-muted-foreground mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-accent hover:text-accent/80 hover:underline font-semibold transition-colors">
            Create one
          </Link>
        </motion.p>
      </motion.div>
    </AuthLayout>
  ) : (<Redirect to="/home" />);
};

export default Page;
